<template lang="pug">
div
	h1 {{contentpage.name}}
	.subtitle-1.mb-2.grey--text
		ws-icon( size="20") thumbs-up
		| {{$tc('page.areas._area.review:review.summary_{recommended}_{average)_{total}', reviews.voteCount, {recommended: reviews.recommendedPercent, average: reviews.average, total: reviews.voteCount})}}
	ws-content(:value="contentpage.content")
		template(slot="features")
			v-row(v-if="goodFeatures.length" dense)
				v-col.shrink
					ws-icon(color="success" size="20") plus
				v-col
					| {{$t('page.areas._area.reviews:rate_destination.good_features')}}:
					|
					glue-list(glue=", " :lastglue="` ${$t('global.and')} `")
						span(v-for="feature in goodFeatures") {{feature}}
					|.
			v-row(v-if="badFeatures.length" dense)
				v-col.shrink
					ws-icon(color="error" size="20") minus
				v-col
					| {{$t('page.areas._area.reviews:rate_destination.bad_features')}}:
					|
					glue-list(glue=", " :lastglue="` ${$t('global.and')} `")
						span(v-for="feature in badFeatures") {{feature}}
					|.
			v-divider.my-6
		template(slot="reviews")
			h2 {{$t('page.areas._area.reviews:rate_destination.heading.{area}', { area: area.name })}}
			add-rating.mb-6(v-bind="{type:'area', id: area.id}" @rated="rated")
			add-review-dialog(v-bind="{type:'area', id: area.id, destination: {name: area.name}}" :initial-rating="rating" v-model="showReviewDialog" @reviewed="reviewed")

			h2.mt-6.mb-2 {{$t('page.areas._area.reviews:review_by_users.heading')}}
			review-list(:items="reviews.nodes")
</template>

<script>
import gql from 'graphql-tag';
import AddRating from '@/components/add-rating';
import AddReviewDialog from '@/components/add-review-dialog';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import ReviewList from '@/components/review-list.vue';
import UserContentDetails from '@/components/user-content/fragments.js';

const getAreaReviews = gql`
	query getAreaReviews($slug: String!, $path: String, $fallback: String) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				markdown
			}
			...MetaFragment
		}
		area(slug: $slug) {
			id
			name
			slug
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			reviews(limit: 60) {
				nodes {
					...UserContentDetails
				}
				voteCount
				average
				recommendedPercent
			}
			features {
				title
				rating
				text
				type
			}
		}
	}
	${UserContentDetails}
	${MetaFragment}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/skigebieden/:area/ervaringen',
			'de-DE': '/skigebiete/:area/erfahrungen',
			'nl-BE': '/skigebieden/:area/ervaringen'
		}
	},
	components: {
		AddRating,
		AddReviewDialog,
		ReviewList
	},
	asyncData({ params, app, route }) {
		return app.$graphClient
			.query({
				query: getAreaReviews,
				variables: {
					slug: params.area,
					path: route.path,
					fallback: route.path.replace(params.area, '_area')
				}
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(404, null, 'area-reviews-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({
					sidebar: {
						visible: true,
						targets: {
							Skiresort: data.area.id,
							State: data.area.region.id,
							Country: data.area.country.id
						}
					}
				});
				app.areaBreadcrumbs(data.area, {
					[data.contentpage.name]: {
						name: 'areas-area-reviews',
						params: { area: data.area.slug }
					}
				});

				return data;
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(500, null, 'area-reviews-02', error);
			});
	},
	data() {
		return { showReviewDialog: false, rating: 0 };
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		goodFeatures() {
			return this.features
				.filter(feature => feature.rating > 2)
				.map(feature => {
					return this.$t(
						`feature_title.${feature.type.toLowerCase()}_${feature.rating}`
					);
				})
				.map((feature, index) => {
					return index === 0
						? feature
						: feature.charAt(0).toLowerCase() + feature.substring(1);
				});
		},
		badFeatures() {
			return this.features
				.filter(feature => feature.rating < 2)
				.map(feature => {
					return this.$t(
						`feature_title.${feature.type.toLowerCase()}_${feature.rating}`
					);
				})
				.map((feature, index) => {
					return index === 0
						? feature
						: feature.charAt(0).toLowerCase() + feature.substring(1);
				});
		},
		reviews() {
			return this.area.reviews;
		},
		features() {
			return this.area.features;
		}
	},
	created() {
		this.$emit('update:showSidebar', true);
	},
	methods: {
		reviewed(review) {
			this.reviews.nodes.unshift(review);
		},
		rated(rating) {
			this.rating = rating;
			this.showReviewDialog = true;
		}
	}
};
</script>
